import { type ChangeEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { useAppSelector } from "hooks/storeHooks"
import { useDebounce } from "hooks/useDebounce"
import { useStableCallback } from "hooks/useStableCallback"
import { useAlert } from "providers/AlertProvider"
import { tintVTOService } from "services/tintvto"
import { capturePlatformEvent } from "services/platformAnalytics"
import { selectUserInfo } from "store/user/userSlice"
import { type GlassesDraftListItem, type Product, type ProductV2 } from "types"
import { parseQuery } from "utils/parseQuery"
import { withListPage } from "utils/withListPage"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { adminApi, type IntegrationQrCodeResponse } from "@/services/adminApi"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FileSpreadsheet, Glasses, Palette, Plus, Search, Share2, Upload, X } from "lucide-react"
import ConfirmDialog from "components/ConfirmDialog"
import EmptyList from "components/EmptyList"
import Paginator from "components/Paginator"
import { AddProductsModal } from "../AddProductsModal"
import { UploadBulkProducts } from "../UploadBulkProducts/UploadBulkProducts"
import { ProductCategories } from "components/Navbar/types"
import {
  ALL_GLASSES_DRAFTS_QUERY_KEY,
  PRODUCTS_QUERY_KEY,
  useGlassesDraftsQuery,
  type ProductsQueryParams,
  useDeleteProductMutation,
  useProductsQuery,
  useToggleProductVisibilityMutation,
} from "hooks/query/useProductsQuery"
import { getGenericUserErrorMessage } from "@/utils/userFacingErrors"
import { isBulkUploadEnabled } from "@/config/typedEnv"
import { GlassesRow } from "./GlassesRow"
import { GlassesSelectionToolbar } from "./GlassesSelectionToolbar"
import { GlassesSetupSteps } from "./GlassesSetupSteps"
import { MakeupRow } from "./MakeupRow"
import { MakeupSelectionToolbar } from "./MakeupSelectionToolbar"
import { MakeupSetupSteps } from "./MakeupSetupSteps"
import { ProductQrCodeDialog } from "../Glasses/ProductQrCodeDialog"
import {
  buildProductDemoUrl,
  isCanonicalPlatformMerchantId,
  isTrustedProductDemoUrl,
  resolveProductDemoVariantId,
} from "./glassesDemoUrl"
import { buildUnifiedGlassesList, type UnifiedGlassesItem } from "./glassesRowModel"
import { clampProductsPage, reconcileSelectedIds, settleBulkActions } from "./productsPageState"

const PAGE_SIZE_OPTIONS = [30, 50, 100]
const PAGE_SIZE_STORAGE_KEY = "productsPageSize"
const productTableHeadClassName = "text-sm font-medium text-foreground"

const buildProductsPageUrl = (pathname: string, search: string, page: number) => {
  const searchParams = new URLSearchParams(search)
  searchParams.set("page", String(page))
  return `${pathname}?${searchParams.toString()}`
}

export const ProductsPage = ({ category }: { category: ProductCategories }) => {
  const navigate = useNavigate()
  const { showAlert } = useAlert()
  const userInfo = useAppSelector(selectUserInfo)
  const { t } = useTranslation()
  const location = useLocation()
  const queryClient = useQueryClient()

  const isGlasses = category === ProductCategories.GLASSES

  const [showAddProductsModal, setShowAddProductsModal] = useState(false)
  const [uploadBulkMode, setUploadBulkMode] = useState(false)
  const [showInlineBulkUpload, setShowInlineBulkUpload] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [draftToDelete, setDraftToDelete] = useState<GlassesDraftListItem | null>(null)
  const [isProductQrDialogOpen, setIsProductQrDialogOpen] = useState(false)
  const [isGeneratingProductQrCode, setIsGeneratingProductQrCode] = useState(false)
  const [productQrCodeError, setProductQrCodeError] = useState("")
  const [productQrCodeName, setProductQrCodeName] = useState<string | undefined>(undefined)
  const [productQrCodeImage, setProductQrCodeImage] = useState<string | undefined>(undefined)
  const [productQrCodeResult, setProductQrCodeResult] = useState<IntegrationQrCodeResponse | null>(
    null
  )
  const [shareNavList, setShareNavList] = useState<
    Array<{ id: string; name: string; cover: string }>
  >([])
  const [shareNavIndex, setShareNavIndex] = useState(0)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [previewRequestPending, setPreviewRequestPending] = useState(false)
  const previewRequestInFlightRef = useRef(false)
  const productListViewedKeyRef = useRef("")
  const selectionRevisionRef = useRef(0)
  const activeSelectionScopeRef = useRef("")
  const [pageSize, setPageSize] = useState(() => {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY)
    return stored ? parseInt(stored) : 30
  })

  const debouncedSearchText = useDebounce<string>(searchText, 300)
  const currentPage = parseInt(parseQuery(location.search).page) || 1
  const productsQueryParams: ProductsQueryParams = {
    page: currentPage,
    searchText: debouncedSearchText,
    category,
    merchantId: userInfo.merchantId ?? "",
    limit: pageSize,
  }
  const { data: productsResponse, isLoading } = useProductsQuery(productsQueryParams)
  const { data: glassesDrafts = [], isLoading: isDraftsLoading } = useGlassesDraftsQuery({
    enabled: isGlasses,
    merchantId: userInfo.merchantId ?? "",
    partial: debouncedSearchText,
  })

  const { pageCount, items: products } = productsResponse || { pageCount: 0, items: [] }
  const drafts = isGlasses ? glassesDrafts : []
  const isPageLoading = isLoading || (isGlasses && isDraftsLoading)
  const hasSavedOrDrafts = products.length > 0 || drafts.length > 0

  const unifiedGlasses = useMemo(
    () => (isGlasses ? buildUnifiedGlassesList(products, drafts) : []),
    [isGlasses, products, drafts]
  )

  const hasActiveGlassesWorkflow =
    isGlasses &&
    (drafts.some(
      (draft) =>
        draft.digitization.status === "queued" || draft.digitization.status === "processing"
    ) ||
      products.some(
        (product) =>
          product.digitization?.status === "queued" || product.digitization?.status === "processing"
      ))

  const hasActiveSearch = debouncedSearchText.length > 0
  const isInitialEmptyMakeup =
    !isGlasses && products.length === 0 && drafts.length === 0 && !hasActiveSearch && !isPageLoading
  const showInlineInitialMakeupActions = isInitialEmptyMakeup

  useEffect(() => {
    if (isPageLoading) return

    const viewKey = `${category}:${currentPage}:${debouncedSearchText}`
    if (productListViewedKeyRef.current === viewKey) return
    productListViewedKeyRef.current = viewKey

    capturePlatformEvent("product_list_viewed", {
      category,
      draft_count: drafts.length,
      has_search: hasActiveSearch,
      page: currentPage,
      page_size: pageSize,
      product_count: products.length,
    })
  }, [
    category,
    currentPage,
    debouncedSearchText,
    drafts.length,
    hasActiveSearch,
    isPageLoading,
    pageSize,
    products.length,
  ])

  const handleAddProduct = () => {
    navigate(withListPage(isGlasses ? "/glasses/add" : "/products/add", location.search))
  }

  const handleOpenUpload = () => {
    setUploadBulkMode(true)
    setShowAddProductsModal(true)
  }

  useEffect(() => {
    if (!showInlineInitialMakeupActions) setShowInlineBulkUpload(false)
  }, [showInlineInitialMakeupActions])

  const clearSelection = () => {
    selectionRevisionRef.current += 1
    setSelectedIds(new Set())
  }

  const handleSearchChange = (value: string) => {
    setSearchText(value)
    clearSelection()
    if (currentPage !== 1)
      navigate(buildProductsPageUrl(location.pathname, location.search, 1), { replace: true })
  }

  const handlePageSizeChange = (value: string) => {
    const newSize = parseInt(value)
    setPageSize(newSize)
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, value)
    clearSelection()
    if (currentPage !== 1)
      navigate(buildProductsPageUrl(location.pathname, location.search, 1), { replace: true })
  }

  const { mutateAsync: toggleVisibilityAsync } =
    useToggleProductVisibilityMutation(productsQueryParams)

  // Stable identity so memoized rows don't re-render when the products array
  // is optimistically replaced; returning the promise lets rows hold their
  // switch disabled until the request settles.
  const toggleRowVisibility = useStableCallback((product: Product) =>
    toggleVisibilityAsync(product)
  )

  const { mutateAsync: deleteProductAsync } = useDeleteProductMutation(productsQueryParams)

  const openProductDemo = async (product: Product, errorKey: string) => {
    const merchantId = userInfo.merchantId?.trim() ?? ""
    if (!isCanonicalPlatformMerchantId(merchantId)) {
      const demoUrl = buildProductDemoUrl(merchantId, product)

      if (!demoUrl) {
        showAlert({ error: true, text: t(errorKey) })
        return
      }

      window.open(demoUrl, "_blank", "noopener,noreferrer")
      return
    }

    if (previewRequestInFlightRef.current) return

    const previewWindow = window.open("about:blank", "_blank")
    if (!previewWindow) {
      showAlert({ error: true, text: t(errorKey) })
      return
    }
    previewWindow.opener = null
    previewRequestInFlightRef.current = true
    setPreviewRequestPending(true)

    try {
      const variantId = resolveProductDemoVariantId(product)
      const { previewUrl } = await adminApi.createTryOnPreviewLink({
        merchantId,
        productId: product.id,
        ...(variantId ? { variantId } : {}),
      })
      if (!isTrustedProductDemoUrl(previewUrl)) throw new Error("Untrusted preview URL")
      if (!previewWindow.closed) previewWindow.location.replace(previewUrl)
    } catch {
      if (!previewWindow.closed) previewWindow.close()
      showAlert({ error: true, text: t(errorKey) })
    } finally {
      previewRequestInFlightRef.current = false
      setPreviewRequestPending(false)
    }
  }

  // Same flow as glasses: a preview link opens the widget STAND
  // (dev/uat/prod tintvto.com) with the publishable key and product already
  // substituted. The QR path used here before was a merge artifact — it sent
  // the demo to the QR landing on the raw API Gateway origin.
  const handleMakeupViewDemo = useStableCallback((product: Product) => {
    void openProductDemo(product, "makeup.action.viewDemoError")
  })

  const handleGlassesViewDemo = useStableCallback((product: Product) => {
    void openProductDemo(product, "glasses.action.viewDemoError")
  })

  const runQrGeneration = async (params: {
    productId?: string
    productName?: string
    productImage?: string
  }) => {
    const merchantId = userInfo.merchantId?.trim()
    if (!merchantId) {
      showAlert({ error: true, text: t("integrationInfo.qrCode.generateError") })
      return
    }

    setIsProductQrDialogOpen(true)
    setIsGeneratingProductQrCode(true)
    setProductQrCodeError("")
    setProductQrCodeResult(null)
    setProductQrCodeName(params.productName)
    setProductQrCodeImage(params.productImage)

    try {
      setProductQrCodeResult(
        await adminApi.generateIntegrationQrCode({
          merchantId,
          ...(params.productId ? { productId: params.productId } : {}),
        })
      )
    } catch {
      setProductQrCodeError(getGenericUserErrorMessage(t))
    } finally {
      setIsGeneratingProductQrCode(false)
    }
  }

  const handleGenerateProductQrCode = useStableCallback((product: Product) => {
    const list = (
      isGlasses
        ? unifiedGlasses.flatMap((item) =>
            item.kind === "product"
              ? [{ id: item.product.id, name: item.product.name, cover: item.product.cover }]
              : []
          )
        : products.map((p) => ({ id: p.id, name: p.name, cover: p.cover }))
    ) as Array<{ id: string; name: string; cover: string }>
    const index = list.findIndex((p) => p.id === product.id)
    setShareNavList(list)
    setShareNavIndex(index < 0 ? 0 : index)
    void runQrGeneration({
      productId: product.id,
      productName: product.name,
      productImage: product.cover,
    })
  })

  const handleShareCatalog = () => {
    setShareNavList([])
    setShareNavIndex(0)
    void runQrGeneration({})
  }

  const handleShareNavigate = (delta: -1 | 1) => {
    const nextIndex = shareNavIndex + delta
    const target = shareNavList[nextIndex]
    if (!target) return
    setShareNavIndex(nextIndex)
    void runQrGeneration({
      productId: target.id,
      productName: target.name,
      productImage: target.cover,
    })
  }

  // The current ?page rides along into the editor so every exit path there
  // can bring the merchant back to the page they left (see utils/withListPage).
  const handleEditProduct = useStableCallback((productId: string) => {
    navigate(withListPage(`${location.pathname}/edit?id=${productId}`, location.search))
  })

  const handleOpenDraft = useStableCallback((draftId: string) => {
    navigate(withListPage(`/glasses/add?draftId=${draftId}`, location.search))
  })

  const toggleRowSelection = useStableCallback((id: string) => {
    selectionRevisionRef.current += 1
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  })

  const currentItemIds = useMemo(
    () => (isGlasses ? unifiedGlasses.map((item) => item.id) : products.map((p) => p.id)),
    [isGlasses, unifiedGlasses, products]
  )
  const visibleSelectedIds = useMemo(
    () => reconcileSelectedIds(selectedIds, currentItemIds),
    [currentItemIds, selectedIds]
  )
  const selectedCount = visibleSelectedIds.size
  const selectionScopeKey = JSON.stringify([category, currentPage, searchText, pageSize])
  activeSelectionScopeRef.current = selectionScopeKey

  const handleToggleAll = (checked: boolean) => {
    selectionRevisionRef.current += 1
    setSelectedIds(() => {
      if (!checked) return new Set()
      return new Set(currentItemIds)
    })
  }

  useEffect(() => {
    clearSelection()
  }, [selectionScopeKey])

  useEffect(() => {
    setSelectedIds((previousIds) => {
      const reconciledIds = reconcileSelectedIds(previousIds, currentItemIds)
      return reconciledIds.size === previousIds.size ? previousIds : reconciledIds
    })
  }, [currentItemIds])

  useEffect(() => {
    if (selectedCount === 0) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") clearSelection()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [selectedCount])

  const selectedGlassesItems = useMemo(
    () => unifiedGlasses.filter((item) => visibleSelectedIds.has(item.id)),
    [unifiedGlasses, visibleSelectedIds]
  )

  const selectableGlassesProducts = useMemo(
    () =>
      selectedGlassesItems.filter(
        (item): item is Extract<UnifiedGlassesItem, { kind: "product" }> =>
          item.kind === "product" && item.status === "ready" && !item.product.visible
      ),
    [selectedGlassesItems]
  )

  const selectedMakeupProducts = useMemo(
    () => products.filter((product) => visibleSelectedIds.has(product.id)),
    [products, visibleSelectedIds]
  )

  const selectableMakeupProducts = useMemo(
    () => selectedMakeupProducts.filter((product) => !product.visible),
    [selectedMakeupProducts]
  )

  const handleBulkTurnOn = async () => {
    const pool: Product[] = isGlasses
      ? selectableGlassesProducts.map((item) => item.product)
      : selectableMakeupProducts
    if (pool.length === 0) return

    const operationScope = activeSelectionScopeRef.current
    const operationSelectionRevision = selectionRevisionRef.current
    setBulkBusy(true)
    try {
      const { succeeded, failed } = await settleBulkActions(pool, (product) =>
        toggleVisibilityAsync(product)
      )

      if (
        activeSelectionScopeRef.current === operationScope &&
        selectionRevisionRef.current === operationSelectionRevision
      )
        setSelectedIds(new Set(failed.map((product) => product.id)))

      const successText = succeeded.length
        ? t(isGlasses ? "glasses.selection.turnedOn" : "makeup.selection.turnedOn", {
            count: succeeded.length,
          })
        : ""

      if (failed.length > 0) {
        const failureText = t("productsPage.bulkTurnOnFailed", {
          count: failed.length,
          defaultValue: "{{count}} product(s) could not be turned on.",
        })
        showAlert({
          error: true,
          text: successText ? `${successText} ${failureText}` : failureText,
        })
      } else {
        showAlert({
          error: false,
          text: successText,
        })
      }
    } finally {
      setBulkBusy(false)
    }
  }

  const performBulkDelete = async () => {
    const glassesItems = isGlasses ? [...selectedGlassesItems] : []
    const makeupItems = !isGlasses ? [...selectedMakeupProducts] : []
    const totalCount = isGlasses ? glassesItems.length : makeupItems.length
    if (totalCount === 0) {
      setBulkDeleteOpen(false)
      return
    }

    setBulkDeleteOpen(false)
    clearSelection()
    setBulkBusy(true)
    let deletedCount = 0
    let failedCount = 0
    try {
      if (isGlasses)
        for (const item of glassesItems)
          try {
            if (item.kind === "product") {
              await deleteProductAsync(item.product as unknown as ProductV2)
            } else {
              await tintVTOService.deleteGlassesDraft(item.draft.draftId)
              queryClient.setQueriesData<GlassesDraftListItem[]>(
                { queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY },
                (old) => old?.filter((d) => d.draftId !== item.draft.draftId)
              )
            }
            deletedCount++
          } catch {
            failedCount++
          }
      else
        for (const product of makeupItems)
          try {
            await deleteProductAsync(product as unknown as ProductV2)
            deletedCount++
          } catch {
            failedCount++
          }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY() }),
        queryClient.invalidateQueries({ queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY }),
      ])

      if (deletedCount > 0)
        showAlert({
          error: false,
          text: t(
            isGlasses ? "glasses.selection.deleteSuccess" : "makeup.selection.deleteSuccess",
            { count: deletedCount }
          ),
        })
      if (failedCount > 0)
        showAlert({
          error: true,
          text: t("productsPage.deleteError"),
        })
    } finally {
      setBulkBusy(false)
    }
  }

  const handleCancelDraft = useStableCallback((draftId: string) => {
    const draft = drafts.find((d) => d.draftId === draftId)
    if (draft) setDraftToDelete(draft)
  })

  const hasProductsOrSearch =
    products.length > 0 || drafts.length > 0 || debouncedSearchText.length > 0 || isPageLoading
  const showEmptyState =
    products.length === 0 &&
    drafts.length === 0 &&
    debouncedSearchText.length === 0 &&
    !isPageLoading

  useEffect(() => {
    if (!productsResponse || isLoading) return

    const nextPage = clampProductsPage(currentPage, pageCount)
    if (nextPage === currentPage) return

    clearSelection()
    navigate(buildProductsPageUrl(location.pathname, location.search, nextPage), {
      replace: true,
    })
  }, [currentPage, isLoading, location.pathname, location.search, pageCount, productsResponse])

  useEffect(() => {
    if (!hasActiveGlassesWorkflow) return

    const intervalId = window.setInterval(() => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY() })
      queryClient.invalidateQueries({ queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY })
    }, 5000)

    const handleFocus = () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY() })
      queryClient.invalidateQueries({ queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY })
    }

    window.addEventListener("focus", handleFocus)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener("focus", handleFocus)
    }
  }, [hasActiveGlassesWorkflow, queryClient])

  const allSelected = currentItemIds.length > 0 && selectedCount === currentItemIds.length
  const indeterminate = selectedCount > 0 && !allSelected
  const showSelectionBar = selectedCount > 0
  const canTurnOnBulk = isGlasses
    ? selectableGlassesProducts.length > 0
    : selectableMakeupProducts.length > 0
  const bulkConfirmText = t(
    isGlasses ? "glasses.selection.confirmDelete" : "makeup.selection.confirmDelete",
    { count: selectedCount }
  )

  const addProductLabel = t("productsPage.addProduct")

  const showUploadCta = !isGlasses && isBulkUploadEnabled
  const productPageTestId = isGlasses ? "products-page-glasses" : "products-page-makeup"
  const productTableTestId = isGlasses ? "products-table-glasses" : "products-table-makeup"

  return (
    <div
      className="mx-auto w-full max-w-[1440px] min-w-0 p-4 md:p-6"
      data-testid={productPageTestId}
    >
      <ConfirmDialog
        isOpen={!!draftToDelete}
        onClose={() => setDraftToDelete(null)}
        onConfirm={() => {
          if (!draftToDelete) return
          const draft = draftToDelete
          setDraftToDelete(null)
          queryClient.setQueriesData<GlassesDraftListItem[]>(
            { queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY },
            (old) => old?.filter((d) => d.draftId !== draft.draftId)
          )
          tintVTOService
            .deleteGlassesDraft(draft.draftId)
            .then(() =>
              showAlert({
                error: false,
                text: t("glasses.draft.cancelSuccess"),
              })
            )
            .catch(() => {
              queryClient.invalidateQueries({ queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY })
              showAlert({
                error: true,
                text: getGenericUserErrorMessage(t),
              })
            })
        }}
        text={t("glasses.draft.cancelConfirm")}
      />
      <ConfirmDialog
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => void performBulkDelete()}
        text={bulkConfirmText}
      />
      <ProductQrCodeDialog
        error={productQrCodeError}
        isGenerating={isGeneratingProductQrCode}
        onOpenChange={setIsProductQrDialogOpen}
        open={isProductQrDialogOpen}
        productName={productQrCodeName}
        productImage={productQrCodeImage}
        result={productQrCodeResult}
        currentIndex={shareNavList.length > 0 ? shareNavIndex : undefined}
        totalCount={shareNavList.length > 0 ? shareNavList.length : undefined}
        onPrevious={shareNavList.length > 0 ? () => handleShareNavigate(-1) : undefined}
        onNext={shareNavList.length > 0 ? () => handleShareNavigate(1) : undefined}
      />
      {showAddProductsModal && (
        <AddProductsModal
          onAlert={showAlert}
          isOpen={showAddProductsModal}
          onClose={() => {
            setShowAddProductsModal(false)
            setUploadBulkMode(false)
          }}
          {...(uploadBulkMode ? { initialStep: 2 as const, initialType: "bulk" as const } : {})}
        />
      )}
      <ProductsPageHeader
        testIdPrefix={isGlasses ? "glasses-products" : "makeup-products"}
        withDivider={showEmptyState}
        title={showEmptyState ? t("productsPage.welcomeTitle") : t("productsPage.setupTitle")}
        steps={
          showEmptyState ? (
            <p className="mt-1 text-sm text-muted-foreground">
              {t("productsPage.welcomeSubtitle")}
            </p>
          ) : isGlasses ? (
            <GlassesSetupSteps />
          ) : (
            <MakeupSetupSteps />
          )
        }
        showSearch={hasSavedOrDrafts || hasActiveSearch}
        showAddCtas={hasSavedOrDrafts}
        showUpload={showUploadCta}
        onUpload={handleOpenUpload}
        onAdd={handleAddProduct}
        addLabel={addProductLabel}
        uploadLabel={t("productsPage.uploadProductList")}
        searchValue={searchText}
        onSearchChange={handleSearchChange}
        searchPlaceholder={t("productsPage.searchPlaceholder")}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        onShareCatalog={hasSavedOrDrafts ? handleShareCatalog : undefined}
        shareCatalogLabel={t("integrationInfo.qrCode.shareCatalog")}
        shareCatalogPlacement="top"
        addVariant="dark"
      />

      {hasProductsOrSearch && !isPageLoading ? (
        isGlasses ? (
          <>
            {unifiedGlasses.length > 0 ? (
              <Card className="min-w-0 overflow-hidden" data-testid={productTableTestId}>
                <Table className="w-full min-w-[940px] table-fixed">
                  <colgroup>
                    <col className="w-[44px]" />
                    <col />
                    <col className="w-[112px]" />
                    <col className="w-[132px]" />
                    <col className="w-[292px]" />
                  </colgroup>
                  <TableHeader>
                    {showSelectionBar ? (
                      <GlassesSelectionToolbar
                        selectedCount={selectedCount}
                        allSelected={allSelected}
                        indeterminate={indeterminate}
                        onToggleAll={handleToggleAll}
                        onTurnOn={() => void handleBulkTurnOn()}
                        onDelete={() => setBulkDeleteOpen(true)}
                        onClear={clearSelection}
                        canTurnOn={canTurnOnBulk}
                        turningOn={bulkBusy}
                        deleting={bulkBusy}
                      />
                    ) : (
                      <TableRow className="group bg-muted/50 hover:bg-muted/50">
                        <TableHead className="pl-3">
                          <HeaderSelectAllCheckbox
                            allSelected={allSelected}
                            indeterminate={indeterminate}
                            onToggle={handleToggleAll}
                          />
                        </TableHead>
                        <TableHead className={productTableHeadClassName}>
                          {t("glasses.table.product")}
                        </TableHead>
                        <TableHead className={productTableHeadClassName}>
                          {t("glasses.table.status")}
                        </TableHead>
                        <TableHead className={productTableHeadClassName}>
                          {t("glasses.table.tryOnButton")}
                        </TableHead>
                        <TableHead className={`${productTableHeadClassName} text-right`}>
                          {t("glasses.table.actions")}
                        </TableHead>
                      </TableRow>
                    )}
                  </TableHeader>
                  <TableBody>
                    {unifiedGlasses.map((item) => (
                      <GlassesRow
                        key={item.id}
                        item={item}
                        selected={visibleSelectedIds.has(item.id)}
                        onToggleSelect={toggleRowSelection}
                        onViewDemo={handleGlassesViewDemo}
                        onEditProduct={handleEditProduct}
                        onGenerateQrCode={handleGenerateProductQrCode}
                        onOpenDraft={handleOpenDraft}
                        onCancelDraft={handleCancelDraft}
                        toggleVisibility={toggleRowVisibility}
                        viewDemoPending={previewRequestPending}
                      />
                    ))}
                  </TableBody>
                </Table>
                {pageCount > 1 && (
                  <div className="border-t px-4 py-3">
                    <Paginator page={currentPage} pageCount={pageCount} />
                  </div>
                )}
              </Card>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-12">
                <p className="text-sm text-muted-foreground">
                  {hasActiveSearch
                    ? t("productsPage.noSearchResults", { query: debouncedSearchText })
                    : t("productsPage.emptyText")}
                </p>
                {hasActiveSearch && (
                  <Button variant="outline" size="sm" onClick={() => handleSearchChange("")}>
                    {t("productsPage.clearSearch")}
                  </Button>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {products.length > 0 ? (
              <Card className="min-w-0 overflow-hidden" data-testid={productTableTestId}>
                <Table className="w-full min-w-[940px] table-fixed">
                  <colgroup>
                    <col className="w-[44px]" />
                    <col />
                    <col className="w-[128px]" />
                    <col className="w-[112px]" />
                    <col className="w-[132px]" />
                    <col className="w-[292px]" />
                  </colgroup>
                  <TableHeader>
                    {showSelectionBar ? (
                      <MakeupSelectionToolbar
                        selectedCount={selectedCount}
                        allSelected={allSelected}
                        indeterminate={indeterminate}
                        onToggleAll={handleToggleAll}
                        onTurnOn={() => void handleBulkTurnOn()}
                        onDelete={() => setBulkDeleteOpen(true)}
                        onClear={clearSelection}
                        canTurnOn={canTurnOnBulk}
                        turningOn={bulkBusy}
                        deleting={bulkBusy}
                      />
                    ) : (
                      <TableRow className="group bg-muted/50 hover:bg-muted/50">
                        <TableHead className="pl-3">
                          <HeaderSelectAllCheckbox
                            allSelected={allSelected}
                            indeterminate={indeterminate}
                            onToggle={handleToggleAll}
                          />
                        </TableHead>
                        <TableHead className={productTableHeadClassName}>
                          {t("makeup.table.product")}
                        </TableHead>
                        <TableHead className={productTableHeadClassName}>
                          {t("makeup.table.type")}
                        </TableHead>
                        <TableHead className={productTableHeadClassName}>
                          {t("makeup.table.status")}
                        </TableHead>
                        <TableHead className={productTableHeadClassName}>
                          {t("makeup.table.tryOnButton")}
                        </TableHead>
                        <TableHead className={`${productTableHeadClassName} text-right`}>
                          {t("makeup.table.actions")}
                        </TableHead>
                      </TableRow>
                    )}
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <MakeupRow
                        key={product.id}
                        product={product}
                        selected={visibleSelectedIds.has(product.id)}
                        onToggleSelect={toggleRowSelection}
                        onViewDemo={handleMakeupViewDemo}
                        onGenerateQrCode={handleGenerateProductQrCode}
                        onEdit={handleEditProduct}
                        toggleVisibility={toggleRowVisibility}
                        viewDemoPending={previewRequestPending}
                      />
                    ))}
                  </TableBody>
                </Table>
                {pageCount > 1 && (
                  <div className="border-t px-4 py-3">
                    <Paginator page={currentPage} pageCount={pageCount} />
                  </div>
                )}
              </Card>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-12">
                <p className="text-sm text-muted-foreground">
                  {hasActiveSearch
                    ? t("productsPage.noSearchResults", { query: debouncedSearchText })
                    : t("productsPage.emptyText")}
                </p>
                {hasActiveSearch && (
                  <Button variant="outline" size="sm" onClick={() => handleSearchChange("")}>
                    {t("productsPage.clearSearch")}
                  </Button>
                )}
              </div>
            )}
          </>
        )
      ) : null}

      {/* Centred as one unit in the free area below the ruled title, not pinned
          under it — same on all three first-run screens. */}
      {showEmptyState && (
        <div
          className="flex min-h-[60vh] items-center justify-center py-12"
          data-testid="products-empty-state"
        >
          {showInlineInitialMakeupActions ? (
            showInlineBulkUpload ? (
              <div className="w-full max-w-2xl" data-testid="products-inline-bulk-upload">
                <UploadBulkProducts
                  onAlert={showAlert}
                  backHandler={() => setShowInlineBulkUpload(false)}
                  onClose={() => setShowInlineBulkUpload(false)}
                />
              </div>
            ) : isBulkUploadEnabled ? (
              <div
                className="mx-auto flex w-full max-w-xl flex-col items-center gap-4"
                data-testid="products-empty-actions"
              >
                <Button
                  size="lg"
                  className="bg-vto-accent text-vto-accent-foreground shadow-sm hover:bg-vto-accent/90 focus-visible:ring-vto-accent"
                  onClick={() => navigate("/products/add")}
                  data-testid="empty-add-first-product"
                >
                  {t("productsPage.addFirstProduct")}
                </Button>
                <span className="text-sm text-muted-foreground">{t("productsPage.or")}</span>
                <div className="flex w-full flex-col items-center gap-4 rounded-xl border border-dashed px-8 py-10">
                  <div className="flex size-14 items-center justify-center rounded-xl bg-muted">
                    <FileSpreadsheet className="size-6" />
                  </div>
                  <p className="max-w-md text-center text-base font-semibold">
                    {t("productsPage.bulkDropTitle")}
                  </p>
                  <Button onClick={() => setShowInlineBulkUpload(true)}>
                    {t("productsPage.uploadProductList")}
                  </Button>
                </div>
              </div>
            ) : (
              // Bulk upload off: the CSV block is what carried the design's
              // framing, so fall back to the same icon-tile treatment glasses
              // and the 3D viewer use instead of a lone floating button.
              <EmptyList
                icon={<Palette className="size-10" />}
                title={t("productsPage.emptyMakeupTitle")}
                addText={t("productsPage.addProductCta")}
                onClick={() => navigate("/products/add")}
                showAdd
              />
            )
          ) : (
            <EmptyList
              icon={<Glasses className="size-10" />}
              title={t("productsPage.emptyGlassesTitle")}
              addText={t("productsPage.addProductCta")}
              onClick={handleAddProduct}
              showAdd={debouncedSearchText === ""}
            />
          )}
        </div>
      )}
      {isPageLoading && (
        <Card className="min-w-0 overflow-hidden" data-testid="products-loading-state">
          <div className="space-y-5 p-6">
            <div className="flex items-center justify-between gap-3">
              <Skeleton width="w-40" height="h-6" />
              <Skeleton width="w-28" height="h-9" />
            </div>
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className={
                    isGlasses
                      ? "grid grid-cols-[44px_1fr_112px_132px_292px] items-center gap-4 border-b pb-3 last:border-b-0 last:pb-0"
                      : "grid grid-cols-[44px_1fr_128px_112px_132px_292px] items-center gap-4 border-b pb-3 last:border-b-0 last:pb-0"
                  }
                >
                  <Skeleton width="w-4" height="h-4" />
                  <Skeleton width="w-48" height="h-10" />
                  {!isGlasses && <Skeleton width="w-16" height="h-4" />}
                  <Skeleton width="w-20" height="h-6" />
                  <Skeleton width="w-24" height="h-4" />
                  <Skeleton width="w-48" height="h-8" />
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}

interface ProductsPageHeaderProps {
  testIdPrefix: string
  title: string
  steps: ReactNode
  showSearch: boolean
  showAddCtas: boolean
  showUpload: boolean
  onUpload: () => void
  onAdd: () => void
  addLabel: string
  uploadLabel: string
  searchValue: string
  onSearchChange: (value: string) => void
  searchPlaceholder: string
  pageSize: number
  onPageSizeChange: (value: string) => void
  onShareCatalog?: () => void
  shareCatalogLabel: string
  shareCatalogPlacement?: "top" | "inline"
  addVariant?: "accent" | "dark"
  /** First-run screens rule off the title, matching the 3D viewer page. */
  withDivider?: boolean
}

const ADD_CTA_WIDTH = "w-[8.625rem]" // 138px
const WIDE_CTA_WIDTH = "w-[11.25rem]" // 180px

const ProductsPageHeader = ({
  testIdPrefix,
  title,
  steps,
  showSearch,
  showAddCtas,
  showUpload,
  onUpload,
  onAdd,
  addLabel,
  uploadLabel,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  pageSize,
  onPageSizeChange,
  onShareCatalog,
  shareCatalogLabel,
  shareCatalogPlacement = "top",
  addVariant = "accent",
  withDivider = false,
}: ProductsPageHeaderProps) => {
  const showShareCatalogInline = shareCatalogPlacement === "inline" && Boolean(onShareCatalog)
  const showShareCatalogTop = shareCatalogPlacement === "top" && Boolean(onShareCatalog)
  const topActions = showShareCatalogTop ? (
    <div className="flex shrink-0 items-center gap-2">
      <Button
        size="sm"
        onClick={onShareCatalog}
        data-testid={`${testIdPrefix}-share-catalog`}
        className={`${WIDE_CTA_WIDTH} bg-vto-accent text-vto-accent-foreground shadow-sm hover:bg-vto-accent/90 focus-visible:ring-vto-accent`}
      >
        <Share2 className="mr-1.5 h-3.5 w-3.5" />
        {shareCatalogLabel}
      </Button>
    </div>
  ) : null

  const addButtonClassName =
    addVariant === "dark"
      ? `${ADD_CTA_WIDTH} bg-foreground text-background shadow-sm hover:bg-foreground/90 focus-visible:ring-foreground`
      : `${ADD_CTA_WIDTH} bg-vto-accent text-vto-accent-foreground shadow-sm hover:bg-vto-accent/90 focus-visible:ring-vto-accent`

  return (
    <div
      className={withDivider ? "mb-6 border-b pb-6" : "mb-6"}
      data-testid={`${testIdPrefix}-header`}
    >
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="products-page-title">
            {title}
          </h1>
          {steps}
        </div>
        {topActions}
      </div>
      {(showSearch || showAddCtas) && (
        <div className="mt-6 flex min-w-0 flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {showSearch && (
              <>
                <div className="relative min-w-[240px] max-w-sm flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/55" />
                  <Input
                    data-testid={`${testIdPrefix}-search`}
                    name="searchbar"
                    value={searchValue}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="h-9 pl-9 text-sm placeholder:text-foreground/55"
                  />
                  {searchValue && (
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground hover:text-foreground"
                      onClick={() => onSearchChange("")}
                      aria-label="Clear search"
                      data-testid={`${testIdPrefix}-clear-search`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <PageSizeSelector
                  value={pageSize}
                  onChange={onPageSizeChange}
                  testId={`${testIdPrefix}-page-size`}
                />
              </>
            )}
          </div>
          {showAddCtas && (
            <div className="flex shrink-0 items-center gap-2">
              {showUpload && (
                <Button
                  size="sm"
                  variant="outline"
                  className={WIDE_CTA_WIDTH}
                  onClick={onUpload}
                  data-testid={`${testIdPrefix}-upload`}
                >
                  <Upload className="mr-1.5 h-3.5 w-3.5" />
                  {uploadLabel}
                </Button>
              )}
              {showShareCatalogInline ? (
                <Button
                  size="sm"
                  onClick={onShareCatalog}
                  data-testid={`${testIdPrefix}-share-catalog`}
                  className={`${WIDE_CTA_WIDTH} bg-vto-accent text-vto-accent-foreground shadow-sm hover:bg-vto-accent/90 focus-visible:ring-vto-accent`}
                >
                  <Share2 className="mr-1.5 h-3.5 w-3.5" />
                  {shareCatalogLabel}
                </Button>
              ) : null}
              <Button
                size="sm"
                onClick={onAdd}
                className={addButtonClassName}
                data-testid={`${testIdPrefix}-add`}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {addLabel}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const PageSizeSelector = ({
  value,
  onChange,
  testId,
}: {
  value: number
  onChange: (value: string) => void
  testId: string
}) => (
  <div className="flex items-center gap-2" data-testid={`${testId}-container`}>
    <span className="text-xs text-foreground/70">Show</span>
    <Select value={String(value)} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-[75px]" data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent data-testid={`${testId}-options`}>
        {PAGE_SIZE_OPTIONS.map((size) => (
          <SelectItem key={size} value={String(size)} data-testid={`${testId}-option-${size}`}>
            {size}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
)

const HeaderSelectAllCheckbox = ({
  allSelected,
  indeterminate,
  onToggle,
}: {
  allSelected: boolean
  indeterminate: boolean
  onToggle: (checked: boolean) => void
}) => (
  <Checkbox
    aria-label="Select all"
    data-testid="products-select-all"
    checked={indeterminate ? "indeterminate" : allSelected}
    className={
      allSelected || indeterminate
        ? ""
        : "opacity-0 transition-opacity group-hover:opacity-60 group-hover:hover:opacity-100 group-focus-within:opacity-100"
    }
    onCheckedChange={(value) => onToggle(Boolean(value))}
  />
)
